import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { PolicySummaryActions } from '@amfam/policy/data-access';
import { PolicyAnalytics } from '@amfam/policy/models';
// Store
import { VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PartyEmail } from '@amfam/shared/models';
import { userQuery, UserState } from '@amfam/shared/user';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ConfirmationModel, DockingBarService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import { ReplaceVehicleService } from '../../../core/store/replace-vehicle/replace-vehicle.service';

@Component({
  selector: 'ds-replace-vehicle-confirmation',
  templateUrl: './replace-vehicle-confirmation.component.html',
  styleUrls: ['./replace-vehicle-confirmation.component.scss'],
  standalone: false
})
export class ReplaceVehicleConfirmationComponent implements OnInit, AfterViewInit {
  public confirmationData: ConfirmationModel;
  public isEligibleAdvancedVehicle = false;
  public myAccountEmail: string;
  public myaccountPrimaryEmail: PartyEmail;
  public user: UserState;
  public isNewCarReplaceCoverage: boolean;
  private agentName: string;
  private vehicleId: string;

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private copyService: CopyService,
    private dockingBarService: DockingBarService,
    private replaceVehicleService: ReplaceVehicleService,
    private store: Store<fromRoot.RootState>,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade,
    private vehicleFacade: VehicleFacade
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');

    combineLatest(
      this.route.params,
      this.store.select(userQuery.selectUserState),
      this.store.select(fromRoot.getReplaceVehicleRisks),
      this.store.select(fromRoot.getReplaceVehicleConfirmationType),
      this.store.select(fromRoot.getReplaceVehiclePremium),
      this.store.select(fromRoot.hasAdvPolicyAndNewCarReplaceCoverage),
      (params, userState, risks, confirmationType, vehiclePremium, isNewCarReplaceCoverage) => ({
        params,
        userState,
        risks,
        confirmationType,
        vehiclePremium,
        isNewCarReplaceCoverage
      })
    )
      .pipe(
        filter(state => !!state.params && !!state.userState && !!state.risks),
        take(1)
      )
      .subscribe(res => {
        this.myAccountEmail = this.getMyAccountEmailAddress(res.userState);
        this.isNewCarReplaceCoverage = res.isNewCarReplaceCoverage;
        if (res.risks) {
          this.vehicleId = res.params['id'];
          const matchingVehicle = res.risks.find(vehicle => _get(vehicle, 'id') === this.vehicleId);
          this.agentName = _get(matchingVehicle, 'policy.assignedProducer.name');
          this.isEligibleAdvancedVehicle =
            this.replaceVehicleService.getAdvancePolicyEligibility(matchingVehicle);
        }
        if (res.confirmationType === 'Online Bind Success') {
          this.buildConfirmationDataForOnlineBindEnabled(
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindSuccessHeading',
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindNextSteps'
          );
          this.store.dispatch(
            new replaceVehicleActions.SendAnalyticsAction({
              pageAnalyticName: 'Confirmation',
              eventName: 'bind complete',
              agentName: this.agentName,
              referenceNumber: _get(res, 'vehiclePremium.workOrderNumber', '')
            })
          );
        } else if (
          res.confirmationType === 'Send Quote to Agent' ||
          res.confirmationType === 'No Quote Send to Agent'
        ) {
          this.buildConfirmationDataForOnlineBindEnabled(
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindAgentNotificationSuccessHeading',
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindAgentNotificationNextSteps'
          );
          this.store.dispatch(
            new replaceVehicleActions.SendAnalyticsAction({
              pageAnalyticName: 'SendToAgent:Success',
              eventName: 'Bind',
              agentName: this.agentName,
              referenceNumber: _get(res, 'vehiclePremium.workOrderNumber', '')
            })
          );
        } else {
          this.buildConfirmationData();
        }
      });
  }

  ngAfterViewInit() {
    this.store
      .select(routerQuery.getRouterPreviousStateUrl)
      .pipe(take(1))
      .subscribe((url: string) => {
        if (url && url.toLowerCase().includes('risk-selection/multiple-policies')) {
          this.analyticsFacade.trackPageAndEvent(
            PolicyAnalytics.pagePolicyAddVehicleNewOrReplaceMultiAdvGraceNotificationSendToAgent,
            PolicyAnalytics.eventPolicyNewOrReplaceMultiAdvGraceNotificationSendToAgent
          );
        } else if (this.isNewCarReplaceCoverage) {
          this.analyticsFacade.trackPage(
            PolicyAnalytics.pagePolicyNewCarReplaceGraceNoticeSentToAgent
          );
        } else {
          this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyNewVehiclePolicyGracePeriod);
        }
      });
  }

  buildConfirmationData() {
    let heading, nextSteps;
    if (this.isEligibleAdvancedVehicle) {
      heading = this.copyService.getCopy('policy.replaceVehicleConfirmation.advanceVehicleHeading');
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.advanceVehicleNextSteps',
        {
          myAccountEmail: this.myAccountEmail
        }
      );
    } else {
      heading = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleHeading'
      );
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleNextSteps'
      );
    }
    this.confirmationData = {
      confirmation: {
        heading: heading
      },
      nextSteps: {
        heading: "You're all set!",
        text: nextSteps
      }
    };
  }

  buildConfirmationDataForOnlineBindEnabled(headdingCopyPath: string, nextStepsCopyPath: string) {
    let heading, nextSteps;
    if (this.isEligibleAdvancedVehicle) {
      heading = this.copyService.getCopy(headdingCopyPath);
      nextSteps = this.copyService.getCopy(nextStepsCopyPath, {
        myAccountEmail: this.myAccountEmail
      });
    } else {
      heading = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleHeading'
      );
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleNextSteps'
      );
    }
    this.confirmationData = {
      confirmation: {
        heading: heading
      },
      nextSteps: {
        heading: "You're all set!",
        text: nextSteps
      }
    };
  }

  done(): void {
    /**
     * AS: If we have replaced a vehicle , we need to refresh the policies to get the latest policies which was bound.
     */
    this.store.dispatch(PolicySummaryActions.LoadSummaries());
    this.vehicleFacade.vehiclePolicyChangeEntryPoint$.pipe(take(1)).subscribe(path => {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [path || '/overview']
        })
      );
    });
  }

  getMyAccountEmailAddress(user) {
    if (user) {
      const emailAddress = _get(user, 'emailAddress', 'your My Account email address');
      return 'at <b>' + emailAddress + '</b>'; // Just making it so that the sentence makes sense if the email address doesn't render.
    } else {
      return 'at your My Account email address'; // In case they end up here while logged out. Remove this if a route guard prevents this
    }
  }
}
